<template>
    <Teleport to="body">
        <div v-if="modelValueInternal" class="modal-backdrop" :style="{ opacity: backgroundOpacity, transitionDuration: currentTransitionDuration + 'ms' }" @touchmove.prevent></div>
        <div
            v-if="modelValueInternal"
            class="adaptive-modal-wrapper"
            :class="{
                mobile: effectivePresentationIsMobile && !forcePopup /* Only for true mobile sheet */,
                'force-popup-mode': forcePopup /* For centering when forcePopup is true */,
                'mobile-forced-popup-padded': isMobile && forcePopup /* New class for wrapper padding */,
            }"
            @mousedown.self="handleBackdropMouseDown"
            @mouseup.self="handleBackdropMouseUp"
        >
            <div
                ref="modalContent"
                class="adaptive-modal-content-shell"
                :class="{
                    'true-mobile-sheet': effectivePresentationIsMobile && !forcePopup,
                    'mobile-full-height': effectivePresentationIsMobile && !forcePopup && mobileFullHeight,
                    'force-popup-full-width-on-mobile': isMobile && forcePopup,
                }"
                :style="{
                    transform: modalTransform,
                    opacity: modalContentOpacity,
                    transition: computedModalContentTransition,
                }"
                @touchstart="handleTouchStart"
                @touchmove="handleTouchMove"
                @touchend="handleTouchEnd"
            >
                <AdaptiveModalHeader v-if="showHeader || $slots['header-extension']" :title="title" :showHeaderInternal="showHeader" :showCloseButtonInternal="showHeader" @close="close">
                    <template #header-extension>
                        <slot name="header-extension"></slot>
                    </template>
                </AdaptiveModalHeader>

                <div ref="scrollableContentArea" class="scrollable-content-area">
                    <slot></slot>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script>
import { mobileUtil } from "@/util/MobileUtil";
import AdaptiveModalHeader from "./AdaptiveModalHeader.vue";

const DESKTOP_ANIMATION_OFFSET = "-100px";
const MOBILE_ANIMATION_OFFSET = "100%";

export default {
    name: "AdaptiveModal",
    components: { AdaptiveModalHeader },
    props: {
        modelValue: {
            type: Boolean,
            required: true,
        },
        title: {
            type: String,
            required: false,
            default: "",
        },
        showHeader: {
            type: Boolean,
            default: true,
        },
        mobileFullHeight: {
            type: Boolean,
            default: false,
        },
        forcePopup: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["update:modelValue", "after-leave"],
    setup() {
        const { isMobile } = mobileUtil();
        return { isMobile };
    },
    data() {
        return {
            touchStartY: 0,
            touchMoveY: 0,
            isDragging: false,
            isDraggingModal: false,
            mouseDownOnBackdrop: false,
            backgroundOpacity: 0,
            modalTransform: "translate3d(0, 100%, 0)",
            modalContentOpacity: 1,
            currentTransitionDuration: 300,
            modelValueInternal: this.modelValue,
            previousTouchMoveY: null,
        };
    },
    computed: {
        effectivePresentationIsMobile() {
            return this.isMobile && !this.forcePopup;
        },
        computedTouchAction() {
            if (!this.effectivePresentationIsMobile) return "auto"; // Use desktop behavior if not effectively mobile
            return this.isDraggingModal ? "none" : "pan-y";
        },
        computedModalContentTransition() {
            if (this.isDragging && this.effectivePresentationIsMobile) {
                // Use effectivePresentationIsMobile
                return "none";
            }
            if (this.effectivePresentationIsMobile) {
                // Use effectivePresentationIsMobile
                return `transform ${this.currentTransitionDuration}ms ease`;
            }
            return `opacity ${this.currentTransitionDuration}ms ease, transform ${this.currentTransitionDuration}ms ease`;
        },
    },
    mounted() {
        this.checkScreenSize();
        this.modalTransform = this.effectivePresentationIsMobile ? `translate3d(0, ${MOBILE_ANIMATION_OFFSET}, 0)` : `translate3d(0, ${DESKTOP_ANIMATION_OFFSET}, 0)`;
        this.modalContentOpacity = this.effectivePresentationIsMobile ? 1 : 0;
    },
    unmounted() {
        if (document.body.classList.contains("modal-open")) {
            this.close();
        }
    },
    watch: {
        modelValue(newVal) {
            this.checkScreenSize();

            if (newVal) {
                const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
                document.documentElement.style.setProperty("--scrollbar-width", `${scrollbarWidth}px`);
                document.documentElement.classList.add("modal-open"); // Add to HTML element
                document.body.classList.add("modal-open");

                this.modelValueInternal = true;
                this.isDragging = false;
                this.isDraggingModal = false;
                this.backgroundOpacity = 0;
                this.modalTransform = this.effectivePresentationIsMobile ? `translate3d(0, ${MOBILE_ANIMATION_OFFSET}, 0)` : `translate3d(0, ${DESKTOP_ANIMATION_OFFSET}, 0)`;
                this.modalContentOpacity = this.effectivePresentationIsMobile ? 1 : 0;

                this.$nextTick(() => {
                    this.$refs.modalContent?.offsetHeight;
                    requestAnimationFrame(() => {
                        this.backgroundOpacity = 1;
                        this.modalTransform = "translate3d(0, 0, 0)";
                        if (!this.effectivePresentationIsMobile) {
                            this.modalContentOpacity = 1;
                        }
                    });
                });
            } else {
                if (document.body.classList.contains("modal-open")) {
                    this.close(); // Trigger full close sequence if not already closing
                }
            }
        },
    },
    methods: {
        checkScreenSize() {
            if (!this.modelValue) {
                this.modalTransform = this.effectivePresentationIsMobile ? `translate3d(0, ${MOBILE_ANIMATION_OFFSET}, 0)` : `translate3d(0, ${DESKTOP_ANIMATION_OFFSET}, 0)`;
                this.modalContentOpacity = this.effectivePresentationIsMobile ? 1 : 0;
            }
        },
        closeInternal() {
            document.body.classList.remove("modal-open");
            document.documentElement.classList.remove("modal-open"); // Remove from HTML element
            document.documentElement.style.removeProperty("--scrollbar-width");

            this.$emit("update:modelValue", false);
            this.$emit("after-leave");
            this.modelValueInternal = false;
        },
        close() {
            this.isDragging = false;
            this.backgroundOpacity = 0;
            this.modalTransform = this.effectivePresentationIsMobile ? `translate3d(0, ${MOBILE_ANIMATION_OFFSET}, 0)` : `translate3d(0, ${DESKTOP_ANIMATION_OFFSET}, 0)`;
            if (!this.effectivePresentationIsMobile) {
                this.modalContentOpacity = 0;
            }
            setTimeout(this.closeInternal, this.currentTransitionDuration);
        },
        handleTouchStart(event) {
            if (!this.effectivePresentationIsMobile) return;

            const target = event.target;

            const modalShell = this.$refs.modalContent;
            const headerElement = modalShell?.querySelector(".adaptive-modal-header"); // Root of AdaptiveModalHeader component

            if (headerElement && headerElement.contains(target)) {
                // Touch started within the bounds of AdaptiveModalHeader (which might just be showing the extension)
                this.isDragging = true;
            } else {
                this.isDragging = false; // Touch started on content or somewhere else
            }

            this.touchStartY = event.touches[0].clientY;
            this.previousTouchMoveY = this.touchStartY;
            this.isDraggingModal = false;
        },
        handleTouchMove(event) {
            if (!this.effectivePresentationIsMobile) return;

            const currentY = event.touches[0].clientY;
            const scrollableArea = this.$refs.scrollableContentArea;

            // Part 1: Handle touches that did NOT start on the header (i.e., started on content area)
            if (!this.isDragging) {
                if (scrollableArea && scrollableArea.contains(event.target)) {
                    const scrollGestureAmount = currentY - this.previousTouchMoveY; // Positive = finger down (content scrolls down)
                    // Negative = finger up   (content scrolls up)

                    // Prevent default if trying to scroll BEYOND the boundaries of the content area.
                    // This helps trap scroll events on iOS, especially when content is short.
                    if (scrollGestureAmount > 0 && scrollableArea.scrollTop <= 0.5) {
                        // Trying to scroll content DOWN when it's ALREADY AT THE TOP
                        if (event.cancelable) event.preventDefault();
                    } else if (scrollGestureAmount < 0 && scrollableArea.scrollTop + scrollableArea.clientHeight >= scrollableArea.scrollHeight - 0.5) {
                        // Trying to scroll content UP when it's ALREADY AT THE BOTTOM
                        if (event.cancelable) event.preventDefault();
                    }
                    // Otherwise, allow native browser scroll for the content if it is scrollable.
                    // `touch-action: pan-y` and `overflow-y: scroll` should facilitate this.
                }
                this.previousTouchMoveY = currentY;
                return;
            }

            // Part 2: Handle touches that DID start on the header (this.isDragging is true)
            // This is for initiating or continuing a modal dismiss-drag.
            if (!scrollableArea) return;

            const deltaYSinceStart = currentY - this.touchStartY;

            // If already actively dragging the modal for dismissal
            if (this.isDraggingModal) {
                if (event.cancelable) {
                    event.preventDefault();
                }
                this.modalTransform = `translate3d(0, ${Math.max(0, deltaYSinceStart)}px, 0)`;
                const maxDrag = 300;
                this.backgroundOpacity = Math.max(0, 1 - Math.max(0, deltaYSinceStart) / maxDrag);
                this.previousTouchMoveY = currentY;
                return;
            }

            // Attempt to initiate modal dismiss-drag (based on header touch and drag amount)
            const DRAG_INITIATION_THRESHOLD = 10;
            // Condition simplified: dismiss can be initiated if dragging down on header, regardless of content scroll.
            if (deltaYSinceStart > DRAG_INITIATION_THRESHOLD) {
                this.isDraggingModal = true;
                if (event.cancelable) {
                    event.preventDefault();
                }
                // When dismissing via header drag, we want to lock the content's scroll.
                // The scrollableArea might not be at scrollTop 0, but its ability to scroll should be neutralized.
                scrollableArea.style.touchAction = "none";
                // We don't force scrollTop = 0 here, as the content can be at any position.

                this.modalTransform = `translate3d(0, ${Math.max(0, deltaYSinceStart)}px, 0)`;
                this.backgroundOpacity = Math.max(0, 1 - Math.max(0, deltaYSinceStart) / 300);
                this.previousTouchMoveY = currentY;
                return;
            }

            // If dragging header but not enough to start dismiss, prevent default to avoid any weird page scroll.
            if (event.cancelable) {
                event.preventDefault();
            }

            this.previousTouchMoveY = currentY;
        },
        handleBackdropMouseDown(event) {
            // Only set flag if the mousedown actually happened on the backdrop (not bubbled)
            if (event.target === event.currentTarget) {
                this.mouseDownOnBackdrop = true;
            }
        },
        handleBackdropMouseUp(event) {
            // Only close if both mousedown and mouseup happened on backdrop
            if (this.mouseDownOnBackdrop && event.target === event.currentTarget) {
                this.close();
            }
            this.mouseDownOnBackdrop = false;
        },
        handleTouchEnd(event) {
            if (!this.effectivePresentationIsMobile) return;
            const scrollableArea = this.$refs.scrollableContentArea;
            if (this.isDraggingModal) {
                const transformMatch = this.modalTransform.match(/translate3d\(0, ([-0-9.]+)px, 0\)/);
                const currentTransformY = transformMatch ? parseFloat(transformMatch[1]) : 0;

                if (currentTransformY > 100) {
                    // Adjusted threshold for safety, was 400
                    this.backgroundOpacity = 0;
                    this.modalTransform = `translate3d(0, ${MOBILE_ANIMATION_OFFSET}, 0)`;
                    setTimeout(this.closeInternal, this.currentTransitionDuration);
                } else {
                    this.backgroundOpacity = 1;
                    this.modalTransform = "translate3d(0, 0, 0)";
                }
            }
            this.isDragging = false;
            this.isDraggingModal = false;
            this.previousTouchMoveY = null;
            if (scrollableArea) {
                scrollableArea.style.touchAction = this.effectivePresentationIsMobile && !this.isDraggingModal ? "pan-y" : "auto";
            }
        },
    },
};
</script>

<style scoped>
.modal-backdrop {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    transition-property: opacity;
    transition-timing-function: ease;
    overflow: hidden;
    touch-action: none;
    -webkit-overflow-scrolling: none;
    overscroll-behavior: none;
}

.adaptive-modal-wrapper {
    position: fixed;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center; /* Default: Center for desktop and forcePopup */
    z-index: 10000;
    background: transparent;
    overflow: hidden;
    touch-action: none;
    -webkit-overflow-scrolling: none;
    overscroll-behavior: none;
}

/* Only for true mobile sheet presentation */
.adaptive-modal-wrapper.mobile {
    align-items: flex-end;
}

/* For forcePopup mode, ensure centering and add padding if on actual mobile */
.adaptive-modal-wrapper.force-popup-mode {
    align-items: center;
}
.adaptive-modal-wrapper.mobile-forced-popup-padded {
    padding: 0 20px; /* 20px padding on left and right, no vertical padding */
    /* If vertical padding is also desired: padding: 20px; */
}

.adaptive-modal-content-shell {
    background: var(--bg-secondary);
    color: var(--text-primary);
    border-radius: 20px; /* Desktop default */
    position: relative;
    max-width: 90%; /* Desktop default */
    max-height: 90vh; /* Desktop default */
    display: flex;
    flex-direction: column;
    will-change: transform, opacity;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    margin-top: 60px; /* Desktop default */
    overflow: hidden;
}

/* Styles for a true mobile bottom sheet */
.adaptive-modal-content-shell.true-mobile-sheet {
    width: 100%;
    max-width: 100%;
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    margin-top: 0;
    overflow-x: hidden;
}

.adaptive-modal-content-shell.true-mobile-sheet.mobile-full-height {
    height: 85vh;
    max-height: 85vh;
}

/* Styles for when forcePopup is true, but on an actual mobile device (popup appearance) */
.adaptive-modal-content-shell.force-popup-full-width-on-mobile {
    width: 100%; /* Fill the padded wrapper */
    max-width: 100%; /* Do not exceed the padded wrapper */
    margin-top: 0;
    border-radius: 20px; /* Rounded corners on all sides like desktop */
    /* max-height might need adjustment if wrapper gets vertical padding, e.g. calc(100% - 40px) or a vh equivalent */
}

.scrollable-content-area {
    flex-grow: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
}

.adaptive-modal-wrapper.mobile:not(.force-popup-mode) {
    align-items: flex-end;
}

.adaptive-modal-wrapper.mobile.mobile-full-height:not(.force-popup-on-mobile) {
    height: 85vh;
    max-height: 85vh;
}

.adaptive-modal-wrapper.mobile .scrollable-content-area {
    overflow-x: hidden;
    overflow-y: scroll;
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.adaptive-modal-wrapper.mobile .scrollable-content-area::-webkit-scrollbar {
    display: none;
}
</style>

<style>
body.modal-open {
    overflow: hidden !important;
    position: relative !important;
    padding-right: var(--scrollbar-width, 0px) !important;
}
</style>

